import { Button, ClickAwayListener, Popper } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { RgbStringColorPicker } from 'react-colorful';
import { FormattedMessage } from 'react-intl';
import RoiColor from '~/components/Structures/RoiColor';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { extractRGBNumbers } from '~/domain/dicom/utils';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useRoi from '~/hooks/structure-set/useRoi';
import { formatColorRgb } from '~/utils/color';

export default function StructureColorPicker({
    roi,
    onChange,
    fieldAnchorRef,
    disabled = false,
    onToggleVisibility,
    onPickerOpenChange,
    popperPlacement = 'bottom-start',
    isTarget = false,
}: {
    roi: RoiReadModel;
    fieldAnchorRef: React.RefObject<HTMLElement | null>;
    onChange: (color: DicomColor) => void;
    disabled?: boolean;
    onToggleVisibility?: () => void;
    onPickerOpenChange?: (open: boolean) => void;
    popperPlacement?: 'bottom-start' | 'top-start';
    isTarget?: boolean;
}) {
    const [colorPickerAnchorEl, setColorPickerAnchorEl] =
        useState<HTMLElement | null>(null);
    const [previousColor, setPreviousColor] = useState<DicomColor>(roi.color);
    const { isRoiEmpty } = useRoi(roi);
    const isEmptyVolume = isRoiEmpty();
    const isMarker = roi.isPointRoi;
    const colorValue = roi.color;

    const handleCancel = useCallback(() => {
        onChange(previousColor);
        setColorPickerAnchorEl(null);
    }, [onChange, previousColor]);

    const handleConfirm = useCallback(() => {
        setPreviousColor(roi.color);
        setColorPickerAnchorEl(null);
    }, [roi.color]);

    const isOpen = !!colorPickerAnchorEl;

    useEffect(() => {
        onPickerOpenChange?.(isOpen);
    }, [isOpen, onPickerOpenChange]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleCancel();
            } else if (event.key === 'Enter') {
                handleConfirm();
            }
        };

        const handleScroll = () => {
            handleConfirm();
        };

        document.addEventListener('keydown', handleKeyDown);
        const scrollContainer = fieldAnchorRef.current?.closest(
            '[data-structure-list]'
        );
        scrollContainer?.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            scrollContainer?.removeEventListener('scroll', handleScroll);
        };
    }, [isOpen, handleCancel, handleConfirm, fieldAnchorRef]);

    const handleColorPickerChange = (newColor: string) => {
        const [R, G, B] = extractRGBNumbers(newColor);
        const newColorObj: DicomColor = { R, G, B };
        onChange(newColorObj);
    };

    const handleColorPickerContextMenu = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) {
            return;
        }
        if (colorPickerAnchorEl) {
            handleCancel();
        } else {
            setPreviousColor(roi.color);
            setColorPickerAnchorEl(event.currentTarget);
        }
    };

    const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (colorPickerAnchorEl) {
            handleCancel();
            return;
        }
        if (!onToggleVisibility) {
            if (!disabled) {
                setPreviousColor(roi.color);
                setColorPickerAnchorEl(event.currentTarget);
            }
            return;
        }
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }
        clickTimerRef.current = setTimeout(() => {
            clickTimerRef.current = null;
            onToggleVisibility();
        }, 200);
    };

    const handleDoubleClick = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }
        if (disabled) {
            return;
        }
        if (!colorPickerAnchorEl) {
            setPreviousColor(roi.color);
            setColorPickerAnchorEl(event.currentTarget);
        }
    };

    return (
        <Box>
            <Popper
                open={!!colorPickerAnchorEl}
                anchorEl={fieldAnchorRef.current}
                placement={popperPlacement}
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 4],
                        },
                    },
                    {
                        name: 'flip',
                        enabled: false,
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 8,
                        },
                    },
                ]}
                sx={{
                    zIndex: 1400,
                }}
            >
                <ClickAwayListener
                    onClickAway={handleConfirm}
                    mouseEvent="onMouseDown"
                >
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            '& .react-colorful': {
                                width: 150,
                                height: 150,
                            },
                        }}
                    >
                        <RgbStringColorPicker
                            color={`rgb(${Object.values(roi.color)})`}
                            onChange={handleColorPickerChange}
                            style={{ width: 150, height: 150 }}
                        />
                        <Box
                            sx={(theme) => ({
                                width: 120,
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: 1,
                                p: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                gap: 1,
                            })}
                        >
                            <Box
                                onClick={() => onChange(previousColor)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    borderRadius: 0.5,
                                    p: 0.5,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                                title={formatColorRgb(previousColor)}
                            >
                                <Box
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        backgroundColor: `rgb(${previousColor.R}, ${previousColor.G}, ${previousColor.B})`,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        flexShrink: 0,
                                    }}
                                />
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            color: 'text.secondary',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        <FormattedMessage id="colorPicker.original" />
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            color: 'text.disabled',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {formatColorRgb(previousColor)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleConfirm}
                                    sx={{
                                        fontSize: 10,
                                        minWidth: 0,
                                        py: 0.25,
                                        px: 0.5,
                                        textTransform: 'none',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <FormattedMessage id="confirm" />
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 9,
                                            opacity: 0.7,
                                            ml: 1,
                                        }}
                                    >
                                        Enter
                                    </Typography>
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleCancel}
                                    sx={{
                                        fontSize: 10,
                                        minWidth: 0,
                                        py: 0.25,
                                        px: 0.5,
                                        textTransform: 'none',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <FormattedMessage id="cancel" />
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: 9,
                                            opacity: 0.7,
                                            ml: 1,
                                        }}
                                    >
                                        Esc
                                    </Typography>
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
            <Box
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                onContextMenu={handleColorPickerContextMenu}
                sx={{
                    cursor: disabled ? 'default' : 'pointer',
                    padding: '8px',
                    margin: '-8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <RoiColor
                    color={colorValue}
                    size={10}
                    isEmptyVolume={isEmptyVolume}
                    isMarker={isMarker}
                    isTarget={isTarget}
                />
            </Box>
        </Box>
    );
}
